import type { CSSProperties, FC } from 'react';
import { useEffect, useRef } from 'react';

import styles from './PulsingCircle.module.css';

const ANIMATION_COMPLETE_TIME = 2700;
const DELAY_BEFORE_RESTART = 2500;
const TOTAL_CYCLE_TIME = ANIMATION_COMPLETE_TIME + DELAY_BEFORE_RESTART;

interface PulsingCircleProps {
  color?: string;
}

export const PulsingCircle: FC<PulsingCircleProps> = ({ color }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const pulseElements = containerRef.current?.querySelectorAll<HTMLDivElement>(`.${styles.pendingIconPulse}`);
    if (!pulseElements) {
      return;
    }

    const handleAnimationCycle = () => {
      pulseElements.forEach((element: HTMLDivElement) => {
        element.style.setProperty('animation', 'none');

        // Trigger reflow to restart animation
        void element.offsetWidth;

        setTimeout(() => {
          element.style.setProperty('animation', '');
        }, 10);
      });
    };

    const initialTimer = setTimeout(() => {
      handleAnimationCycle();
      intervalIdRef.current = setInterval(handleAnimationCycle, TOTAL_CYCLE_TIME);
    }, TOTAL_CYCLE_TIME);
    return () => {
      clearTimeout(initialTimer);
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={styles.pendingIconWrapper}
      style={color ? ({ '--pulsing-circle-color': color } as CSSProperties) : undefined}
    >
      <div className={styles.pendingIcon} />
      <div className={`${styles.pendingIconPulse} ${styles.pulse1}`} />
      <div className={`${styles.pendingIconPulse} ${styles.pulse2}`} />
      <div className={`${styles.pendingIconPulse} ${styles.pulse3}`} />
    </div>
  );
};
